// Professional qualification wallet. It deliberately separates documents that
// prove a nurse's professional capability from CNE attendance records. A
// qualification can link to a CNE record, but neither replaces the other.
const QUALIFICATION_CATEGORIES = [
  {
    id: 'practising', code: 'RN / EN',
    en: 'Practising credentials', zh: '執業資格',
    enSub: 'NCHK registration and practising certificates', zhSub: '護士管理局註冊、登記及執業證明',
    examples: ['RN', 'EN', 'Midwife'],
  },
  {
    id: 'specialty', code: 'APN',
    en: 'Specialty qualifications', zh: '專科資格',
    enSub: 'APN, PRCC and professional specialty recognition', zhSub: 'APN、PRCC、專科護理及院士資格',
    examples: ['APN', 'PRCC', 'HKANM'],
  },
  {
    id: 'education', code: 'BSc',
    en: 'Education', zh: '學歷',
    enSub: 'Nursing diplomas, degrees and postgraduate study', zhSub: '護理文憑、學士、碩士、博士及相關學歷',
    examples: ['Diploma', 'BSc', 'MSc / PhD'],
  },
  {
    id: 'resuscitation', code: 'BLS',
    en: 'Resuscitation & first aid', zh: '急救與復甦',
    enSub: 'BLS, ACLS, PALS, NRP, CPR/AED and first aid', zhSub: 'BLS、ACLS、PALS、NRP、CPR/AED 及急救',
    examples: ['BLS', 'ACLS', 'PALS'],
  },
  {
    id: 'trauma', code: 'TNCC',
    en: 'Trauma & emergency', zh: '創傷與急症',
    enSub: 'ATLS, ITLS, PHTLS, TNCC, ENPC and PEARS', zhSub: 'ATLS、ITLS、PHTLS、TNCC、ENPC、PEARS',
    examples: ['ATLS', 'TNCC', 'PHTLS'],
  },
  {
    id: 'clinical', code: 'IV',
    en: 'Clinical skills', zh: '臨床技能',
    enSub: 'Hospital clinical competency certifications', zhSub: '院內臨床能力認證',
    examples: ['IV', 'ECG', 'Wound care'],
  },
  {
    id: 'other', code: '＋',
    en: 'Other professional qualification', zh: '其他專業資格',
    enSub: 'A credential not listed above', zhSub: '未列出的專業資格',
    examples: ['Custom'],
  },
];

const QUALIFICATION_PRESETS = {
  practising: [
    ['NCHK RN registration / enrolment', '香港護士管理局 RN 註冊／登記證明'],
    ['NCHK EN registration / enrolment', '香港護士管理局 EN 註冊／登記證明'],
    ['NCHK electronic practising certificate', '護士管理局電子執業證明書'],
    ['Midwife registration / practising certificate', '助產士註冊及執業證明書'],
  ],
  specialty: [
    ['Advanced Practice Nurse (APN) recognition', '專科護師（APN）認可'],
    ['PRCC qualification', '專科護理認可（PRCC）'],
    ['Hospital Authority specialty nursing certificate', '醫管局專科護理證書'],
    ['HKANM Fellowship / specialty qualification', 'HKANM 院士／專科資格'],
  ],
  education: [
    ['Nursing diploma', '護理文憑'],
    ['Bachelor of Nursing', '護理學士'],
    ['Master of Nursing', '護理碩士'],
    ['Doctoral degree in nursing', '護理博士'],
  ],
  resuscitation: [
    ['Basic Life Support (BLS)', '基礎生命支援（BLS）'],
    ['Advanced Cardiovascular Life Support (ACLS)', '高級心臟生命支援（ACLS）'],
    ['Paediatric Advanced Life Support (PALS)', '兒科高級生命支援（PALS）'],
    ['Neonatal Resuscitation Program (NRP)', '新生兒復甦課程（NRP）'],
    ['CPR / AED / First Aid / ILS / ALSO', 'CPR／AED／急救／ILS／ALSO'],
  ],
  trauma: [
    ['ATLS / ITLS / PHTLS', 'ATLS／ITLS／PHTLS'],
    ['Trauma Nursing Core Course (TNCC)', '創傷護理核心課程（TNCC）'],
    ['Emergency Nursing Pediatric Course (ENPC)', '兒科急症護理課程（ENPC）'],
    ['PEARS', '兒科急症評估、識別及穩定化（PEARS）'],
  ],
  clinical: [
    ['Venepuncture / IV cannulation', '抽血／靜脈置管'],
    ['Blood transfusion competency', '輸血能力認證'],
    ['Chemotherapy administration competency', '化療給藥能力認證'],
    ['Dialysis / ventilator / ECG competency', '洗腎／呼吸機／ECG 能力認證'],
    ['Wound, stoma or infection-control competency', '傷口／造口／感染控制能力認證'],
  ],
  other: [],
};

// Existing wallets used the specific acronym as `kind`. Keep old rows visible
// and migrate them to the new umbrella category naturally on their next edit.
const LEGACY_QUALIFICATION_KINDS = { bls: 'resuscitation', acls: 'resuscitation', pals: 'resuscitation', nrp: 'resuscitation' };

function normaliseQualificationKind(kind) {
  return LEGACY_QUALIFICATION_KINDS[kind] || (QUALIFICATION_CATEGORIES.some(x => x.id === kind) ? kind : 'other');
}

// Reading a stored row always yields a category (unknown → 'other'). The ADD
// form must NOT: `normaliseQualificationKind(undefined)` returns 'other', which
// pre-selected 其他專業資格 before the nurse had chosen anything and then filed
// the credential under it. In the editor an empty kind means "not chosen yet"
// and the save button refuses until she picks one.
function editorQualificationKind(kind) {
  return kind ? normaliseQualificationKind(kind) : '';
}

// ‼️ Raw PostgREST/plpgsql text must never reach the sheet. It is English-only
// in a Chinese UI, it is not actionable ("invalid kind"), and it describes our
// schema to anyone reading the screen. Every failure the RPCs can raise gets a
// localised sentence that says what to do next; anything unrecognised falls
// back to a generic retry line rather than leaking the message.
//
// `invalid kind` / 23514 specifically means the DATABASE has not been migrated
// to the category taxonomy yet (supabase-migration-qualification-taxonomy.sql,
// RELEASE.md §49) while this client already offers the seven categories. The
// copy names the one category that still saves so she is not simply stuck.
function qualificationSaveErrorMessage(error, lang) {
  const zh = lang === 'zh';
  const raw = String(error?.message || '');
  const code = String(error?.code || '');
  const has = (...needles) => needles.some(n => raw.toLowerCase().includes(n));
  if (has('invalid kind', 'invalid qualification') || (code === '23514' && has('kind'))) {
    return zh
      ? '此分類暫時未能儲存(伺服器仍在更新)。你可以先選「其他專業資格」儲存,稍後再改回正確分類。'
      : 'This category can’t be saved yet (the server is still updating). Save it under “Other professional qualification” for now and change it later.';
  }
  // ‼️ 23505 IS THE ONE THE NURSE CAN ACT ON, and it fell through to "try again
  // later" — advice that could never work, because a unique violation is not a
  // transient failure. `unique (source_record_id)` fires when a CNE record she
  // has already filed is added a second time, which the wallet actively invited:
  // the "CNE certificates you can add" list derives its candidates from the
  // qualifications array, so before the wiring fix that list never shrank and
  // kept suggesting rows she had already saved. She would tap it, be told the
  // save failed, and reasonably try again.
  if (code === '23505' || has('duplicate key value', 'already exists',
                              'qualification_versions_source_record_id_key')) {
    return zh
      ? '這項資格已經在你的資格錢包中,不需要重複新增。'
      : 'This qualification is already in your wallet — no need to add it again.';
  }
  if (has('title required')) return zh ? '請填寫資格名稱。' : 'Add a qualification name.';
  if (has('expiry before issue date')) return zh ? '有效期不能早於簽發日期。' : 'The expiry date can’t be before the issue date.';
  if (has('permanent qualification cannot expire')) return zh ? '已選「不設到期日」,請清除有效期。' : 'You chose “does not expire” — clear the expiry date.';
  if (has('not authenticated') || code === '42501') return zh ? '請重新登入後再試。' : 'Please sign in again and retry.';
  if (has('failed to fetch', 'networkerror', 'network request failed')) {
    return zh ? '網絡不穩定,資料未儲存。請檢查連線後再試。' : 'Network problem — nothing was saved. Check your connection and retry.';
  }
  return zh ? '儲存失敗,請稍後再試。' : 'Could not save — please try again.';
}

function qualificationCategory(kind) {
  const normalised = normaliseQualificationKind(kind);
  return QUALIFICATION_CATEGORIES.find(x => x.id === normalised) || QUALIFICATION_CATEGORIES[QUALIFICATION_CATEGORIES.length - 1];
}

function qualificationKindLabel(kind, lang) {
  const legacy = { bls: 'BLS', acls: 'ACLS', pals: 'PALS', nrp: 'NRP' };
  if (legacy[kind]) return legacy[kind];
  const category = qualificationCategory(kind);
  return lang === 'zh' ? category.zh : category.en;
}

function qualificationTitle(qualification, lang) {
  return qualification?.title?.[lang] || qualification?.title?.en || qualification?.titleEn || qualificationKindLabel(qualification?.kind, lang);
}

// The grid is a single-choice control, so it is a radiogroup — not the
// role="list" of buttons it used to be (buttons are not listitems, and
// aria-pressed made every card read as an independent toggle).
function QualificationCategoryPicker({ lang, value, onChange, compact = false }) {
  const active = editorQualificationKind(value);
  if (compact) {
    return (
      <select className="input" value={active}
        aria-label={lang === 'zh' ? '資格分類' : 'Qualification category'}
        onChange={e => onChange(e.target.value)}>
        {!active && <option value="" disabled>{lang === 'zh' ? '請選擇分類' : 'Choose a category'}</option>}
        {QUALIFICATION_CATEGORIES.map(category => (
          <option key={category.id} value={category.id}>{lang === 'zh' ? category.zh : category.en}</option>
        ))}
      </select>
    );
  }
  return (
    <div className="qualification-category-grid" role="radiogroup"
      aria-label={lang === 'zh' ? '資格分類' : 'Qualification categories'}>
      {QUALIFICATION_CATEGORIES.map(category => (
        <button key={category.id} type="button" role="radio"
          className={'qualification-category-card' + (active === category.id ? ' is-selected' : '')}
          onClick={() => onChange(category.id)} aria-checked={active === category.id}>
          <span className="qualification-category-code" aria-hidden="true">{category.code}</span>
          <span className="qualification-category-copy">
            <strong>{lang === 'zh' ? category.zh : category.en}</strong>
            <small>{lang === 'zh' ? category.zhSub : category.enSub}</small>
          </span>
          {active === category.id && <Icon name="check" size={15} />}
        </button>
      ))}
    </div>
  );
}

// Once she has chosen, the seven-card wall collapses to one row. The grid is
// half the sheet's height; leaving it open pushed every field the form actually
// needs below the fold and made 儲存資格 a scroll away from the choice.
function QualificationCategorySummary({ lang, kind, onChange }) {
  const category = qualificationCategory(kind);
  return (
    <div className="qualification-category-summary">
      <span className="qualification-category-code" aria-hidden="true">{category.code}</span>
      <span className="qualification-category-copy">
        <strong>{lang === 'zh' ? category.zh : category.en}</strong>
        <small>{lang === 'zh' ? category.zhSub : category.enSub}</small>
      </span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onChange}>
        {lang === 'zh' ? '更改' : 'Change'}
      </button>
    </div>
  );
}

function QualificationNamePresets({ lang, kind, title, onChoose }) {
  const presets = kind ? (QUALIFICATION_PRESETS[normaliseQualificationKind(kind)] || []) : [];
  if (!presets.length) return null;
  return (
    <div className="qualification-presets">
      <div className="field-label">{lang === 'zh' ? '常見資格（可選）' : 'Common credentials (optional)'}</div>
      {/* Wraps rather than scrolls: the horizontal row silently hid the last
          chips behind the sheet edge, so the presets a nurse most needs were
          the ones she could not see. */}
      <div className="qualification-preset-row">
        {presets.map(([en, zh]) => (
          <button key={en} type="button"
            className={'filter-chip' + (title === (lang === 'zh' ? zh : en) ? ' on' : '')}
            onClick={() => onChoose(lang === 'zh' ? zh : en)}>{lang === 'zh' ? zh : en}</button>
        ))}
      </div>
    </div>
  );
}

// The raw <input type="file"> rendered as the platform's grey 「選擇檔案」 control
// — the only unstyled element in the sheet, and on iOS it gives no way to undo
// a wrong pick. This wraps it: the input still does the work, hidden.
function QualificationEvidenceField({ lang, file, onChange }) {
  const inputRef = React.useRef(null);
  return (
    <div className="field">
      <label className="field-label">{lang === 'zh' ? '證書檔案（選填）' : 'Certificate file (optional)'}</label>
      <input ref={inputRef} className="sr-only" type="file" accept="image/*,application/pdf"
        tabIndex={-1} aria-hidden="true"
        onChange={e => onChange(e.target.files?.[0] || null)} />
      {file ? (
        <div className="qualification-file-picked">
          <Icon name="pdf" size={16} />
          <span className="qualification-file-name">{file.name}</span>
          <button type="button" className="icon-btn"
            aria-label={lang === 'zh' ? '移除檔案' : 'Remove file'}
            onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ''; }}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ) : (
        <button type="button" className="qualification-file-btn" onClick={() => inputRef.current?.click()}>
          <Icon name="upload" size={16} />
          <span>{lang === 'zh' ? '上載相片或 PDF' : 'Upload a photo or PDF'}</span>
        </button>
      )}
    </div>
  );
}

function QualificationEditorSheet({ lang, qualification, mode = 'add', onClose, onSave, onUpdate }) {
  const zh = lang === 'zh';
  const current = qualification?.current || qualification?.currentVersion || qualification?.versions?.find(v => v.isCurrent) || null;
  const initialKind = editorQualificationKind(qualification?.kind);
  const [kind, setKind] = React.useState(initialKind);
  const [title, setTitle] = React.useState(qualification ? qualificationTitle(qualification, lang) : '');
  const [issuer, setIssuer] = React.useState(current?.issuer?.[lang] || current?.issuer?.en || current?.issuerEn || '');
  const [issuedOn, setIssuedOn] = React.useState(current?.issuedOn || '');
  const [expiresOn, setExpiresOn] = React.useState(current?.expiresOn || '');
  const [permanent, setPermanent] = React.useState(!!current?.doesNotExpire || initialKind === 'education');
  const [notes, setNotes] = React.useState(current?.notes || '');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState({});
  // Collapsed once a category exists, so edit/renew and the CNE-record path
  // open straight onto the fields rather than onto the seven-card wall.
  const [pickerOpen, setPickerOpen] = React.useState(!initialKind);
  const permanentTouched = React.useRef(!!current);
  // One idempotency key per open sheet, NOT per attempt. `saveQualification`
  // mints a fresh uuid when the payload carries none, so a retry after a
  // timeout — the exact case p_request_id exists for — used to look like a
  // brand-new save and could file the credential twice.
  const requestIdRef = React.useRef(globalThis.crypto?.randomUUID?.() || null);
  const editing = mode === 'edit';

  const chooseKind = (next) => {
    setKind(next);
    setPickerOpen(false);
    setFieldErrors(e => ({ ...e, kind: '' }));
    // 學歷 never expires. Set the toggle for her the first time, but never
    // again once she has expressed her own preference.
    if (!permanentTouched.current) setPermanent(next === 'education');
  };

  const validate = () => {
    const next = {};
    if (!kind) next.kind = zh ? '請選擇資格分類。' : 'Choose a category.';
    if (!title.trim()) next.title = zh ? '請填寫資格名稱。' : 'Add a qualification name.';
    if (!permanent && !expiresOn) next.expiresOn = zh ? '請填寫有效期,或勾選「此資格不設到期日」。' : 'Add an expiry date, or tick “does not expire”.';
    // Mirrors the DB CHECK so she is told here rather than after a round trip.
    if (!permanent && expiresOn && issuedOn && expiresOn < issuedOn) {
      next.expiresOn = zh ? '有效期不能早於簽發日期。' : 'The expiry date can’t be before the issue date.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    setError('');
    if (!validate()) {
      if (!kind) setPickerOpen(true);
      return;
    }
    setBusy(true);
    const payload = {
      qualificationId: qualification?.id,
      kind,
      titleEn: title.trim(),
      titleZh: zh ? title.trim() : (qualification?.title?.zh || ''),
      issuerEn: issuer.trim(),
      issuedOn: issuedOn || null,
      expiresOn: permanent ? null : expiresOn,
      doesNotExpire: permanent,
      notes: notes.trim() || null,
      requestId: requestIdRef.current,
    };
    const result = editing ? await onUpdate?.(current?.id, payload) : await onSave?.(payload, file);
    setBusy(false);
    if (result?.error) { setError(qualificationSaveErrorMessage(result.error, lang)); return; }
    // The row is saved; only the attachment failed. Saying "saved" and nothing
    // else would leave her believing the certificate image is in the wallet.
    if (result?.evidenceError) {
      setError(zh ? '資格已儲存,但證書檔案上載失敗。你可以稍後再上載。' : 'The qualification was saved, but the certificate file did not upload. You can add it later.');
      return;
    }
    onClose?.();
  };

  return (
    <Sheet open={true} onClose={onClose}
      title={editing ? (zh ? '編輯資格' : 'Edit qualification') : mode === 'renew' ? (zh ? '新增續期版本' : 'Add renewal') : (zh ? '新增資格' : 'Add qualification')}>
      {/* Every other sheet in the app wraps its children in this padding; this
          one did not, so the whole form ran edge to edge against the screen. */}
      <div className="qualification-sheet-body">
        <div className="qualification-editor">
          {!editing && mode !== 'renew' && (
            <div className="qualification-editor-intro">
              <Icon name="shield" size={18} stroke={1.8} />
              <span>{zh ? '先選分類，再填寫或選擇資格名稱。' : 'Choose a category first, then name the credential.'}</span>
            </div>
          )}
          <div className="field">
            <label className="field-label">
              {zh ? '資格分類' : 'Category'} <span className="field-required" aria-hidden="true">*</span>
            </label>
            {(editing || mode === 'renew')
              ? <QualificationCategoryPicker lang={lang} value={kind} onChange={chooseKind} compact />
              : (pickerOpen || !kind)
                ? <QualificationCategoryPicker lang={lang} value={kind} onChange={chooseKind} />
                : <QualificationCategorySummary lang={lang} kind={kind} onChange={() => setPickerOpen(true)} />}
            {fieldErrors.kind && <div role="alert" className="qualification-error">{fieldErrors.kind}</div>}
          </div>
          <QualificationNamePresets lang={lang} kind={kind} title={title} onChoose={t => { setTitle(t); setFieldErrors(e => ({ ...e, title: '' })); }} />
          <div className="field">
            <label className="field-label" htmlFor="qual-title">
              {zh ? '資格名稱' : 'Qualification name'} <span className="field-required" aria-hidden="true">*</span>
            </label>
            <input id="qual-title" className="field-input" value={title}
              aria-invalid={!!fieldErrors.title}
              onChange={e => { setTitle(e.target.value); setFieldErrors(x => ({ ...x, title: '' })); }}
              placeholder={zh ? '例如：高級心臟生命支援（ACLS）' : 'e.g. Advanced Cardiovascular Life Support (ACLS)'} />
            {fieldErrors.title && <div role="alert" className="qualification-error">{fieldErrors.title}</div>}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="qual-issuer">{zh ? '簽發機構（選填）' : 'Issuer (optional)'}</label>
            <input id="qual-issuer" className="field-input" value={issuer} onChange={e => setIssuer(e.target.value)}
              placeholder={zh ? '例如：American Heart Association' : 'e.g. American Heart Association'} />
          </div>
          <div className="qualification-date-grid">
            <div className="field">
              <label className="field-label">{zh ? '簽發日期（選填）' : 'Issued on (optional)'}</label>
              <DateField lang={lang} value={issuedOn} onChange={setIssuedOn} max={expiresOn || undefined} />
            </div>
            {!permanent && <div className="field">
              <label className="field-label">
                {zh ? '有效期至' : 'Expires on'} <span className="field-required" aria-hidden="true">*</span>
              </label>
              <DateField lang={lang} value={expiresOn} onChange={v => { setExpiresOn(v); setFieldErrors(x => ({ ...x, expiresOn: '' })); }} min={issuedOn || undefined} />
            </div>}
          </div>
          {fieldErrors.expiresOn && <div role="alert" className="qualification-error">{fieldErrors.expiresOn}</div>}
          <label className="toggle-row qualification-toggle">
            <input type="checkbox" checked={permanent}
              onChange={e => { permanentTouched.current = true; setPermanent(e.target.checked); setFieldErrors(x => ({ ...x, expiresOn: '' })); }} />
            <span>{zh ? '此資格不設到期日' : 'This qualification does not expire'}</span>
          </label>
          <div className="field">
            <label className="field-label" htmlFor="qual-notes">{zh ? '備註（選填）' : 'Notes (optional)'}</label>
            <textarea id="qual-notes" className="field-input" value={notes} onChange={e => setNotes(e.target.value)} rows="3"
              placeholder={zh ? '例如：證書編號、續期安排' : 'e.g. certificate number, renewal plan'} />
          </div>
          {!editing && <QualificationEvidenceField lang={lang} file={file} onChange={setFile} />}
          {/* ‼️ This note is a PRIVACY promise, and every clause below is one the
              code actually keeps — do not widen it without re-checking:
              • qualifications / qualification_versions carry an own-rows-only
                SELECT policy and no client write policy at all;
              • the evidence file lands in the private `certificates` bucket
                under <uid>/… , gated by the "certs own read" storage policy;
              • delete-account walks that same <uid>/ prefix on erasure.
              It also keeps the original honesty clause: storing is not
              verifying. */}
          <div className="qualification-privacy-note" role="note">
            <Icon name="shield" size={16} stroke={1.8} />
            <div>
              <strong>{zh ? '只有你看得到' : 'Only you can see this'}</strong>
              <span>{zh
                ? 'Vital 不會公開你的資格,不會分享給僱主、醫院或其他護士,也不會用於社群課程庫。上載的證書檔案存放在私人空間,只有你的帳戶能開啟;刪除帳戶時會一併清除。這裡的資料只用於你自己的紀錄與到期提醒 — 儲存不代表證書已被核實。'
                : 'Vital never publishes your qualifications, never shares them with employers, hospitals or other nurses, and never uses them in the community catalogue. Any file you upload is stored privately and can only be opened by your own account; deleting your account erases it too. This is only for your own records and expiry reminders — saving does not verify a credential.'}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Sticky: the form is taller than the sheet, and the primary action used
          to sit below the fold behind the tab bar. */}
      <div className="qualification-editor-foot">
        {error && <div role="alert" className="qualification-error">{error}</div>}
        <LoadingButton className="btn btn-primary btn-fill" isLoading={busy} onClick={submit}>
          {zh ? '儲存資格' : 'Save qualification'}
        </LoadingButton>
      </div>
    </Sheet>
  );
}

// The certificate behind a wallet card — and the way to add one after the fact.
//
// ‼️ EVIDENCE USED TO BE A ONE-SHOT AT CREATION. `QualificationEvidenceField`
// is rendered only by the ADD form (`{!editing && …}`), so a nurse who saved a
// BLS card first and found the paper afterwards had nowhere to put it — and one
// she HAD uploaded was never shown again anywhere in the app. The wallet was
// holding a file it would not display and would not let her replace.
//
// Same bucket, same RPC as the add path (uploadQualificationEvidence →
// set_qualification_evidence, upsert on <uid>/qualifications/<q>/<version>), so
// this opens a second door onto the existing contract rather than a second way
// to store a certificate.
function QualificationVersionEvidence({ lang, qualification, version, userId, onChanged }) {
  const zh = lang === 'zh';
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [url, setUrl] = React.useState(null);
  const [viewing, setViewing] = React.useState(false);
  const fileRef = React.useRef(null);
  const path = version ? version.evidencePath : null;
  const isPdf = evidenceIsPdf({ storagePath: path });

  React.useEffect(() => {
    let cancelled = false;
    setUrl(null);
    if (!path) return undefined;
    (async () => {
      const signed = await (window.DB?.getCertificateUrl?.(path) || Promise.resolve(null));
      if (!cancelled) setUrl(signed || null);
    })();
    return () => { cancelled = true; };
  }, [path]);

  const pick = async (file) => {
    if (!file || busy || !version?.id || !userId) return;
    setBusy(true); setErr('');
    try {
      const res = await window.DB.uploadQualificationEvidence(userId, qualification.id, version.id, file);
      if (res?.error) { setErr(zh ? '上載失敗,請稍後再試。' : 'Upload failed — please try again.'); return; }
      await onChanged?.();
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div>
      <div className="eyebrow qualification-section-label">{zh ? '證書' : 'Certificate'}</div>
      {path && (
        <button type="button" className="qualification-evidence-preview"
          disabled={!url} onClick={() => setViewing(true)}
          aria-label={zh ? '放大查看證書' : 'View certificate full screen'}>
          {url && !isPdf
            ? <img src={url} alt="" />
            : <span className="qualification-evidence-placeholder">
                <Icon name={isPdf ? 'pdf' : 'cert'} size={26} stroke={1.6} />
                <span>{isPdf ? 'PDF' : (url === null ? (zh ? '載入中…' : 'Loading…') : (zh ? '無法顯示預覽' : 'Preview unavailable'))}</span>
              </span>}
        </button>
      )}
      <input ref={fileRef} className="sr-only" type="file" accept="image/*,application/pdf"
        tabIndex={-1} aria-hidden="true" onChange={e => pick(e.target.files?.[0] || null)} />
      <button type="button" className="qualification-file-btn" disabled={busy || !userId || !version?.id}
        onClick={() => fileRef.current?.click()}>
        <Icon name="upload" size={16} />
        <span>{busy ? (zh ? '上載中…' : 'Uploading…')
          : path ? (zh ? '更換證書' : 'Replace certificate')
          : (zh ? '上載證書' : 'Upload certificate')}</span>
      </button>
      {err ? <div className="auth-error" role="alert">{err}</div> : null}
      {viewing && url && <EvidenceViewer lang={lang} src={url} isPdf={isPdf} onClose={() => setViewing(false)} />}
    </div>
  );
}

function QualificationDetailSheet({ lang, qualification, userId, onClose, onSave, onUpdate, onArchive, onDeleteVersion, onEvidenceChanged }) {
  const [mode, setMode] = React.useState(null);
  // ‼️ BEFORE THE `if (mode) return` BELOW. A hook after a conditional return is
  // a conditional hook call — the render-order violation eslint's
  // rules-of-hooks exists to catch, and the one that ships as a blank screen.
  const confirmer = useConfirm();
  const versions = [...(qualification?.versions || [])].sort((a, b) => String(b.issuedOn || b.createdAt || '').localeCompare(String(a.issuedOn || a.createdAt || '')));
  const current = qualification?.current || qualification?.currentVersion || versions.find(v => v.isCurrent) || versions[0];
  if (mode) return <QualificationEditorSheet lang={lang} qualification={qualification} mode={mode} onClose={() => setMode(null)} onSave={onSave} onUpdate={onUpdate} />;
  const status = qualificationStatus(qualification);
  return (
    <Sheet open={true} onClose={onClose} title={qualificationTitle(qualification, lang)}>
      <div className="qualification-sheet-body">
      <div className="qualification-detail">
        <div className="qualification-detail-hero">
          <div className="eyebrow">{qualificationKindLabel(qualification.kind, lang)}</div>
          <div className="serif qualification-detail-date">{current?.doesNotExpire ? (lang === 'zh' ? '不設到期日' : 'No expiry') : fmtDate(current?.expiresOn, lang) || '—'}</div>
          <div className="muted qualification-status-copy">{status.key === 'expired' ? (lang === 'zh' ? '已到期' : 'Expired') : status.days != null ? (lang === 'zh' ? `尚餘 ${status.days} 日` : `${status.days} days remaining`) : (lang === 'zh' ? '目前版本' : 'Current version')}</div>
        </div>
        <div className="qualification-detail-actions"><button className="btn btn-secondary" onClick={() => setMode('edit')}>{lang === 'zh' ? '編輯' : 'Edit'}</button><button className="btn btn-primary" onClick={() => setMode('renew')}>{lang === 'zh' ? '新增續期' : 'Add renewal'}</button></div>
        <QualificationVersionEvidence lang={lang} qualification={qualification} version={current}
          userId={userId} onChanged={onEvidenceChanged} />
        <div><div className="eyebrow qualification-section-label">{lang === 'zh' ? '版本紀錄' : 'Version history'}</div>{versions.map((v, i) => <div key={v.id} className="qualification-version-card"><div><div className="h-row">{v.doesNotExpire ? (lang === 'zh' ? '不設到期日' : 'No expiry') : fmtDate(v.expiresOn, lang) || '—'}</div><div className="muted">{v.issuer?.[lang] || v.issuer?.en || ''}{v.sourceRecordId ? (lang === 'zh' ? ' · 已連結 CNE 紀錄' : ' · Linked CNE record') : ''}</div></div>{i > 0 && <button className="icon-btn" aria-label={lang === 'zh' ? '刪除舊版本' : 'Delete old version'} onClick={() => confirmer.ask({
                tone: 'danger',
                title: lang === 'zh' ? '刪除這個舊版本？' : 'Delete this old version?',
                subject: (v.doesNotExpire
                  ? (lang === 'zh' ? '不設到期日' : 'No expiry')
                  : (fmtDate(v.expiresOn, lang) || '—')),
                subjectDetail: v.issuer?.[lang] || v.issuer?.en || '',
                body: lang === 'zh'
                  ? '只會刪除這個版本紀錄，你目前的資格不受影響。無法復原。'
                  : 'Only this version record is removed — your current qualification is unaffected. This cannot be undone.',
                confirmLabel: lang === 'zh' ? '刪除' : 'Delete',
                failureText: lang === 'zh' ? '刪除失敗' : 'Delete failed',
                onConfirm: () => onDeleteVersion?.(v.id),
              })}><Icon name="trash" size={15} /></button>}</div>)}</div>
        <button className="btn btn-ghost btn-fill" onClick={() => onArchive?.(qualification.id, !qualification.archivedAt)}>{qualification.archivedAt ? (lang === 'zh' ? '恢復資格' : 'Restore qualification') : (lang === 'zh' ? '封存資格' : 'Archive qualification')}</button>
      </div>
      </div>
      <ConfirmDialog request={confirmer.request} busy={confirmer.busy} lang={lang}
        onCancel={confirmer.cancel} onConfirm={confirmer.confirm} />
    </Sheet>
  );
}

function QualificationWalletCard({ lang, active, attention, ready = true, onAdd, onAiScan }) {
  return (
    <div className="qualification-wallet-card">
      <div className="qualification-wallet-copy">
        <div className="eyebrow">{lang === 'zh' ? '我的專業檔案' : 'MY PROFESSIONAL RECORD'}</div>
        <div className="h-row">{lang === 'zh' ? '資格錢包' : 'Qualification wallet'}</div>
        <div className="muted">{!ready
          ? (lang === 'zh' ? '載入中…' : 'Loading…')
          : (lang === 'zh' ? `${active.length} 項資格${attention ? ` · ${attention} 項需留意` : ''}` : `${active.length} qualification${active.length === 1 ? '' : 's'}${attention ? ` · ${attention} need attention` : ''}`)}</div>
      </div>
      <div className="qualification-wallet-actions">
        <button type="button" className="btn btn-soft" onClick={onAiScan}><Icon name="sparkle" size={15} />{lang === 'zh' ? 'AI 掃描' : 'AI scan'}</button>
        <button type="button" className="btn btn-primary" onClick={onAdd}><Icon name="plus" size={15} />{lang === 'zh' ? '新增' : 'Add'}</button>
      </div>
    </div>
  );
}

// The focus decision, as a pure function so it can be tested against states
// rather than only through a rendered component. Exported on window for
// scripts/test-qualification-load-state.mjs.
//
// ‼️ THE THREE ANSWERS ARE NOT SYMMETRIC.
//   hold    — we do not know yet. Keep the intent; claim nothing.
//   open    — found it.
//   missing — looked, and it is genuinely not there.
// Only the last two consume the intent. Holding through loading is the whole
// point: a cert_expiry push can arrive before the wallet has loaded, and the
// previous code answered "missing" to a question it had not yet asked.
// Sort order across a mixed wallet: risk first, then everything else. Practising
// licences and qualifications share one scale because they share one list.
function walletItemRank(item) {
  const st = walletItemState(item);
  if (st.needsAttention) return st.expiry && st.expiry.days < 0 ? 0 : 1;
  if (st.identityNeedsResolution) return 2;
  if (st.isIncomplete) return 3;
  return 4;
}

function resolveFocusIntent({ ready, focusId, qualifications, answeredFor }) {
  if (!focusId) return { action: 'idle', consume: false };
  if (!ready) return { action: 'hold', consume: false };
  if (answeredFor === focusId) return { action: 'idle', consume: false };
  const found = (qualifications || []).find(x => x && x.id === focusId) || null;
  return found
    ? { action: 'open', consume: true, qualification: found }
    : { action: 'missing', consume: true, qualification: null };
}

// ‼️ THREE STATES, AND ONLY ONE OF THEM MAY SAY "NOTHING".
//   loading      — not authoritative yet. No count, no empty state, no "missing".
//   ready + rows — the list.
//   ready + none — the genuine empty state.
// `ready` is passed in rather than inferred from the array, because an array
// cannot distinguish "not asked yet" from "asked, and she owns nothing" — which
// is exactly how a plausible 0 got rendered over real data once already.

// Which viewer a stored document needs. `contentType` is what the browser
// reported at upload time and is the authority; the path extension is the
// fallback, because the column is nullable and older rows carry no type.
function evidenceIsPdf(doc) {
  const type = String((doc && doc.contentType) || '');
  if (type) return /pdf/i.test(type);
  return /\.pdf(?:$|[?#])/i.test(String((doc && doc.storagePath) || ''));
}

// ─────────────────────────────────────────────────────────────────────────────
// <EvidenceViewer> — the certificate itself, full-bleed, INSIDE the app.
//
// ‼️ THIS EXISTS BECAUSE window.open() CANNOT DO THE JOB HERE. A stored document
// is only reachable through a signed URL, and minting one is asynchronous — so
// by the time open() ran, the user-gesture token from the 查看 tap had already
// expired and iOS Safari swallowed the popup without a word. The button looked
// dead. `noopener` made it undiagnosable too: Chromium returns null from
// open() BY SPEC, so a null result proves nothing about whether it was blocked.
// Rendering the document ourselves removes the whole class of failure.
//
// ‼️ PORTALED TO <body>, for the same reason DeleteRecordConfirm portals: the
// Sheet that opens this is `overflow: hidden`, and at tablet+ widths it keeps a
// permanent translate(-50%,-50%), which makes it the containing block for any
// `position: fixed` descendant. Rendered in place the viewer would be clipped
// to the sheet instead of covering the screen.
// ─────────────────────────────────────────────────────────────────────────────
function EvidenceViewer({ lang, src, isPdf, onClose }) {
  const zh = lang === 'zh';
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Capture phase, and stop it here: `Sheet` also listens for Escape on
      // window, so without this one key press would close both layers and she
      // would lose the sheet she was working in.
      e.preventDefault();
      e.stopPropagation();
      onClose && onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return window.ReactDOM.createPortal(
    <div className="evidence-viewer" role="dialog" aria-modal="true"
      aria-label={zh ? '證書' : 'Certificate'} onClick={onClose}>
      <button type="button" className="evidence-viewer-close" onClick={onClose}
        aria-label={zh ? '關閉' : 'Close'}>
        <Icon name="close" size={18} stroke={2} />
      </button>
      {isPdf ? (
        <div className="evidence-viewer-pdf" onClick={e => e.stopPropagation()}>
          <iframe src={src} title={zh ? '證書 PDF' : 'Certificate PDF'} />
          {/* ‼️ A REAL <a href>, NEVER a scripted popup. iOS Safari renders only
              the first page of a PDF in an iframe, so the escape hatch has to
              exist — and it cannot be window.open() for exactly the reason this
              component was written. An anchor carries the tap's own gesture and
              is never blocked. */}
          <a className="btn btn-ghost btn-sm" href={src} target="_blank" rel="noopener noreferrer">
            {zh ? '在瀏覽器開啟' : 'Open in browser'}
          </a>
        </div>
      ) : (
        <img src={src} alt={zh ? '證書' : 'Certificate'} onClick={e => e.stopPropagation()} />
      )}
    </div>,
    document.body);
}

// The one place a nurse manages the paper for a licence (§72 C5–C7).
//
// ‼️ THE PERIOD TOKEN IS CAPTURED WHEN THIS SHEET OPENS AND NEVER REFRESHED.
// She can leave this open, renew in another session, and come back to finish the
// upload. Filing that document against the NEW period would make
// covers_renewal_date a claim the system invented rather than a fact about the
// paper — so a changed period is refused and re-asked, exactly as the rollover
// writer does.
//
// ‼️ AND THIS SHEET WRITES NO LICENCE FACT. It uploads, attaches, views and
// deletes documents. It never touches a renewal date, a registration type, or
// anything the Profile owns — which is the property that let evidence ship while
// AI update-existing stayed behind the STOP LINE.
function LicenceEvidenceSheet({ lang, item, userId, evidence, onClose, onChanged }) {
  const zh = lang === 'zh';
  const confirmer = useConfirm();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [viewing, setViewing] = React.useState(null);
  const [currentUrl, setCurrentUrl] = React.useState(null);
  const fileRef = React.useRef(null);
  // Frozen at open. See the note above.
  const periodRef = React.useRef(item?.expiresOn || null);

  const current = currentLicenceEvidence(evidence, item);
  const history = licenceEvidenceHistory(evidence, item).filter(e => e && e.id !== (current && current.id));

  // ‼️ THE DOCUMENT SHOWS ITSELF, WITHOUT BEING ASKED. A row that says 「本期
  // 證書」 is a claim about a file she cannot see; the file is the only thing
  // that proves the claim, and a nurse checking whether she uploaded the RIGHT
  // certificate should not have to open anything to find out. The signed URL is
  // resolved once per document here and reused by 查看, so opening the full
  // view costs no second round trip.
  const currentPath = current ? current.storagePath : null;
  React.useEffect(() => {
    let cancelled = false;
    setCurrentUrl(null);
    if (!currentPath) return undefined;
    (async () => {
      const url = await (window.DB?.getLicenceEvidenceUrl?.(currentPath) || Promise.resolve(null));
      if (!cancelled) setCurrentUrl(url || null);
    })();
    return () => { cancelled = true; };
  }, [currentPath]);

  const message = (status) => {
    switch (status) {
      case 'stalePeriod': return zh
        ? '你的執業證書期間已更新。請重新開啟此頁,確認這份文件屬於哪一期。'
        : 'Your certificate period changed. Reopen this page and confirm which period this document proves.';
      case 'identityNeedsResolution': return zh
        ? '此執業資格的類別剛有更改,需要先確認,之後才能上載證書。'
        : 'This licence has a registration-type change awaiting review — confirm it before uploading.';
      case 'identityUnavailable': return zh
        ? '找不到此執業資格,請重新載入後再試。' : 'This licence could not be found — reload and try again.';
      case 'noPeriodToProve': return zh
        ? '此執業證書未有續期日期,請先在個人資料補上。'
        : 'This licence has no renewal date yet — add it in your profile first.';
      default: return zh ? '上載失敗,請稍後再試。' : 'Upload failed — please try again.';
    }
  };

  const pick = async (file) => {
    if (!file || busy) return;
    setBusy(true); setErr('');
    try {
      const res = await window.DB.uploadLicenceEvidence({
        userId, licenseInstanceId: item.licenseInstanceId,
        expectedRenewalDate: periodRef.current, file,
      });
      if (res.error) { setErr(message(res.status)); return; }
      // ‼️ STAY OPEN. This sheet used to close on a successful upload, which
      // meant the one moment she most wants to check her work — did the right
      // page go up, is it legible — was the one moment the document was not on
      // screen. The refreshed read lands underneath and the preview appears.
      await onChanged?.();
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const view = async (doc) => {
    if (!doc) return;
    const cached = current && doc.id === current.id ? currentUrl : null;
    const url = cached || await window.DB.getLicenceEvidenceUrl(doc.storagePath);
    if (!url) { setErr(zh ? '暫時無法開啟文件,請稍後再試。' : 'Could not open the document — try again shortly.'); return; }
    setErr('');
    setViewing({ url, isPdf: evidenceIsPdf(doc) });
  };

  // ‼️ CONFIRM BEFORE DESTROYING. 刪除 sits one tap from 查看 on a phone, and
  // the two together are 25px apart; the delete is not undoable — the row goes
  // and delete_licence_evidence takes the blob with it. This is the same guard
  // the version-history delete above uses. It is deliberately NOT
  // DeleteRecordConfirm: that dialog is shaped around a CNE record and speaks
  // about points and the NCHK export, neither of which a certificate has.
  // ‼️ THE MOST EXPOSED OF THE SIX. This is a nurse, not an admin, deleting the
  // document that proves her practising licence. If window.confirm wedges her
  // WebView (§94) she has no reason to guess that a reload is the way out — and
  // §75 already burned this exact sheet once, when 查看 died silently on iOS.
  const remove = (doc) => confirmer.ask({
    tone: 'danger',
    title: zh ? '刪除這份執業證書？' : 'Delete this certificate?',
    subject: doc.fileName || (zh ? '執業證書' : 'Practising certificate'),
    body: zh ? '此動作無法復原,但你可以重新上載。'
             : 'This cannot be undone, but you can upload it again.',
    confirmLabel: zh ? '刪除' : 'Delete',
    failureText: zh ? '刪除失敗,請稍後再試。' : 'Could not delete — please try again.',
    onConfirm: () => removeNow(doc),
  });

  const removeNow = async (doc) => {
    setBusy(true); setErr('');
    try {
      const res = await window.DB.deleteLicenceEvidence({ evidenceId: doc.id, storagePath: doc.storagePath });
      if (res.error) { setErr(zh ? '刪除失敗,請稍後再試。' : 'Could not delete — please try again.'); return; }
      // The viewer may be showing the file we just deleted; its signed URL is
      // now a link to nothing.
      setViewing(null);
      await onChanged?.();
    } finally { setBusy(false); }
  };

  const registration = item.registrationType || (zh ? '執業資格' : 'Practising licence');

  return (
    <Sheet open={true} onClose={onClose} title={zh ? '執業證書' : 'Practising certificate'}>
      <div className="qualification-sheet-body">
        {/* ‼️ THE WHOLE CARD OPENS THIS SHEET NOW, so the sheet has to say which
            licence she tapped. A dual-registered nurse who renewed both
            registrations together holds two certificates expiring on the SAME
            day — that is the common case, not an edge one — so the period below
            does not identify the card on its own. */}
        <div className="qualification-detail-hero">
          <div className="eyebrow">{item.slot === 'secondary'
            ? (zh ? '第二註冊' : 'Second registration')
            : (zh ? '主要註冊' : 'Primary registration')}</div>
          <div className="serif qualification-detail-date">{fmtDate(item.expiresOn, lang) || '—'}</div>
          <div className="muted qualification-status-copy">
            {registration}{item.licenseNo ? ` · ${item.licenseNo}` : ''}
          </div>
        </div>

        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {zh ? `這份文件會標示為證明「有效至 ${fmtDate(item.expiresOn, lang)}」這一期。`
              : `This document will be recorded as proof of the period valid to ${fmtDate(item.expiresOn, lang)}.`}
        </div>

        {current ? (
          <div>
            <button type="button" className="qualification-evidence-preview"
              onClick={() => view(current)}
              aria-label={zh ? '放大查看證書' : 'View certificate full screen'}>
              {currentUrl && !evidenceIsPdf(current)
                ? <img src={currentUrl} alt="" />
                : <span className="qualification-evidence-placeholder">
                    <Icon name={evidenceIsPdf(current) ? 'pdf' : 'cert'} size={26} stroke={1.6} />
                    <span>{evidenceIsPdf(current) ? 'PDF' : (currentUrl === null ? (zh ? '載入中…' : 'Loading…') : (zh ? '無法顯示預覽' : 'Preview unavailable'))}</span>
                  </span>}
            </button>
            <div className="qualification-file-picked">
              <Icon name="cert" size={16} />
              <span className="qualification-file-name">
                {zh ? `本期證書 · ${fmtDate(current.coversRenewalDate, lang)}` : `This period · ${fmtDate(current.coversRenewalDate, lang)}`}
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => view(current)}>{zh ? '查看' : 'View'}</button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => remove(current)}>{zh ? '刪除' : 'Delete'}</button>
            </div>
          </div>
        ) : null}

        <input ref={fileRef} className="sr-only" type="file" accept="image/*,application/pdf"
          tabIndex={-1} aria-hidden="true" onChange={e => pick(e.target.files?.[0] || null)} />
        <button type="button" className="qualification-file-btn" disabled={busy}
          onClick={() => fileRef.current?.click()}>
          <Icon name="upload" size={16} />
          <span>{busy ? (zh ? '上載中…' : 'Uploading…')
            : current ? (zh ? '更換證書' : 'Replace certificate')
            : (zh ? '上載執業證書' : 'Upload certificate')}</span>
        </button>

        {/* Earlier periods are history, not clutter. A certificate that proved
            2027 is still proof of 2027 after she renews into 2030 — that is what
            covers_renewal_date bought, and hiding it would waste it. */}
        {history.length > 0 && (
          <div className="qualification-candidates">
            <div className="eyebrow">{zh ? '較早期別' : 'Earlier periods'}</div>
            {history.map(doc => (
              <div key={doc.id} className="qualification-file-picked">
                <span className="qualification-file-name">{fmtDate(doc.coversRenewalDate, lang)}</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => view(doc)}>{zh ? '查看' : 'View'}</button>
              </div>
            ))}
          </div>
        )}

        {err ? <div className="auth-error" role="alert">{err}</div> : null}
      </div>
      {viewing && <EvidenceViewer lang={lang} src={viewing.url} isPdf={viewing.isPdf}
        onClose={() => setViewing(null)} />}
      <ConfirmDialog request={confirmer.request} busy={confirmer.busy} lang={lang}
        onCancel={confirmer.cancel} onConfirm={confirmer.confirm} />
    </Sheet>
  );
}

function QualificationsView({ lang, qualifications = [], items = null, ready = true, records = [], licenceEvidence = [], licenceEvidenceReady = false, onEvidenceChanged, userId, onSave, onUpdate, onArchive, onDeleteVersion, onAiScan, onEditLicence, focusQualificationId, onFocusHandled }) {
  const [editor, setEditor] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  // ‼️ MISSING IS A DATA CONCLUSION, NOT A TIMING EVENT.
  // A cert_expiry push routes here with a qualificationId. The previous version
  // consumed that intent unconditionally — so if it arrived before the wallet
  // had loaded, `find` returned undefined, the sheet never opened, and the id
  // was thrown away. The nurse tapped "BLS expires in 7 days" and landed on a
  // screen showing nothing, with no second chance.
  //
  // Now the intent survives loading and is consumed exactly once, when there is
  // an answer either way. Consuming on "ready + absent" matters too: without it
  // a genuinely deleted qualification would re-trigger this effect forever.
  const [missingFocus, setMissingFocus] = React.useState(false);
  const [evidenceFor, setEvidenceFor] = React.useState(null);
  const focusDone = React.useRef(null);
  React.useEffect(() => {
    const decision = resolveFocusIntent({
      ready, focusId: focusQualificationId, qualifications, answeredFor: focusDone.current,
    });
    if (!decision.consume) return;
    focusDone.current = focusQualificationId;
    if (decision.action === 'open') setSelected(decision.qualification);
    else setMissingFocus(true);
    onFocusHandled?.();
  }, [focusQualificationId, qualifications, ready, onFocusHandled]);
  // ‼️ ONE UNIVERSE, PASSED IN. `items` is walletItems() built once in app.jsx —
  // the same array the personal-page count reads. Deriving a second list here is
  // exactly what made Personal say 1 while this screen said 0, and the fix was
  // never "pass the same state": it was "stop deriving twice".
  const universe = items || [];
  const active = universe.slice().sort((a, b) => walletItemRank(a) - walletItemRank(b));
  const filtered = filter === 'all' ? active : active.filter(i =>
    i.kind === 'practising_licence'
      ? filter === 'practising'
      : normaliseQualificationKind(i.qualificationKind) === filter);
  const linked = new Set((qualifications || []).flatMap(q => (q.versions || []).map(v => v.sourceRecordId).filter(Boolean)));
  const candidates = records.filter(r => !linked.has(r.id) && !!qualificationKindFromTitle(L(r.title, 'en')));
  // ‼️ `missing_expiry` is GONE from this list, deliberately. Counting "we do not
  // know when it expires" as a risk warning is C3's whole point: a nurse who
  // never finished onboarding got the same red badge as one whose card expires
  // this week. Attention now comes from walletItemState, which decides it only
  // from a date we actually have.
  const attention = active.filter(i => walletItemState(i).needsAttention).length;
  return (
    <div className="qualification-wallet">
      <QualificationWalletCard lang={lang} active={active} attention={attention} ready={ready} onAdd={() => setEditor({ mode: 'add' })} onAiScan={onAiScan} />
      <div className="qualification-ai-context">
        <Icon name="sparkle" size={16} stroke={1.8} />
        <div><strong>{lang === 'zh' ? '用 AI 掃描證書，更快建立資格' : 'Scan a certificate to add it faster'}</strong><span>{lang === 'zh' ? 'AI 會讀取名稱、簽發機構和日期並建議分類；儲存前仍由你核對。' : 'AI reads the title, issuer and dates, then suggests a category. You review before saving.'}</span></div>
      </div>
      <div className="qualification-filter-row" aria-label={lang === 'zh' ? '篩選資格分類' : 'Filter qualification categories'}>
        <button type="button" className={'filter-chip' + (filter === 'all' ? ' on' : '')} onClick={() => setFilter('all')}>{lang === 'zh' ? '全部' : 'All'} <span>{ready ? active.length : '—'}</span></button>
        {QUALIFICATION_CATEGORIES.slice(0, -1).map(category => <button key={category.id} type="button" className={'filter-chip' + (filter === category.id ? ' on' : '')} onClick={() => setFilter(category.id)}>{lang === 'zh' ? category.zh : category.en}</button>)}
      </div>
      {missingFocus && (
        <div className="qualification-filter-empty" role="status">
          {lang === 'zh' ? '找不到該項資格,它可能已被刪除。' : 'That qualification could not be found — it may have been deleted.'}
        </div>
      )}
      {!ready ? (
        // Loading says nothing. Not "0 項資格", not the empty state, not a CTA —
        // every one of those is a claim about data we do not have yet.
        <div className="qualification-filter-empty" aria-busy="true">
          {lang === 'zh' ? '載入中…' : 'Loading…'}
        </div>
      ) : active.length === 0 ? (
        <div className="qualification-empty-state">
          <Icon name="shield" size={32} />
          <div className="h-row">{lang === 'zh' ? '建立你的專業資格錢包' : 'Build your professional wallet'}</div>
          <div className="muted">{lang === 'zh' ? '集中保存執業證明、專科資格、學歷、急救證書及院內能力認證。' : 'Keep practising credentials, specialty qualifications, education, resuscitation cards and clinical competencies together.'}</div>
          <div className="qualification-empty-actions"><button type="button" className="btn btn-primary" onClick={onAiScan}><Icon name="sparkle" size={15} />{lang === 'zh' ? '掃描第一張證書' : 'Scan your first certificate'}</button><button type="button" className="btn btn-ghost" onClick={() => setEditor({ mode: 'add' })}>{lang === 'zh' ? '手動新增' : 'Add manually'}</button></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="qualification-filter-empty">{lang === 'zh' ? '此分類暫未有資格。' : 'No qualifications in this category yet.'}</div>
      ) : filtered.map(item => {
        // ── The practising licence card (§72 C4) ──────────────────────────
        // ‼️ COMPLETE-FIRST. Measured: 208 of 254 occupied slots have a renewal
        // date, so the filled card is the normal case and 補上資料 is the
        // exception — the opposite of the working assumption before the numbers
        // were taken.
        //
        // ‼️ AND THE CTA READS `missing`, NOT `isIncomplete`. 236 of 254 slots
        // carry no licence number, so prompting on isIncomplete would put
        // 補上資料 on 93% of cards and drown the 46 that genuinely cannot do
        // their job. A missing number is real data quality; it is not a reason
        // to interrupt this card.
        if (item.kind === 'practising_licence') {
          const st = walletItemState(item);
          const needsDate = st.missing.includes('renewalDate');
          const badges = <>
            {st.identityNeedsResolution && <span className="cap-pill">{lang === 'zh' ? '待確認' : 'Review'}</span>}
            {st.needsAttention && <span className="cap-pill tone-warning">{lang === 'zh' ? '快到期' : 'Due'}</span>}
          </>;
          const code = <span className="qualification-list-code">{item.slot === 'secondary' ? 'RM' : 'RN'}</span>;
          const name = item.registrationType || (lang === 'zh' ? '執業資格' : 'Practising licence');

          // ‼️ THE WHOLE ROW IS THE CONTROL, NOT A BUTTON PARKED INSIDE IT.
          // The row used to be a static div carrying one small action button, a
          // shape left over from the period when there was no evidence layer to
          // open. It taught the wrong thing: tapping the card — the obvious
          // gesture, and the one every other card in this list answers — did
          // nothing, and the only live target was a 60px button beside the
          // badges. The card now opens the certificate sheet, which is where
          // viewing, uploading and replacing all live.
          //
          // The upload CTA is not lost by this: it is the sheet's primary
          // button, one tap further in and impossible to miss, and the row
          // states which of the two states she is in so the tap is informed.
          if (!needsDate) {
            const doc = licenceEvidenceReady ? currentLicenceEvidence(licenceEvidence, item) : null;
            // ‼️ THREE STATES AGAIN, AND FOR THE SAME REASON. Until the evidence
            // read lands we do not know whether she has filed anything, so the
            // card says nothing about it — no 「未上載」, which would be a claim,
            // and no tap target, which would open a sheet that cannot answer.
            // Guessing and correcting half a second later is what B2 was about,
            // one layer up.
            //
            // An identity awaiting review offers no upload either: evidence
            // cannot settle which registration this is, and filing a certificate
            // against a question would be the wrong way round.
            const canOpen = licenceEvidenceReady && !st.identityNeedsResolution;
            const validTo = lang === 'zh' ? `有效至 ${fmtDate(item.expiresOn, lang)}` : `Valid to ${fmtDate(item.expiresOn, lang)}`;
            if (canOpen) {
              return <button key={item.id} type="button" className="qualification-list-card"
                onClick={() => setEvidenceFor(item)}>
                {code}
                <span className="qualification-list-copy">
                  <strong>{name}</strong>
                  <small>{validTo} · {doc
                    ? (lang === 'zh' ? '已上載證書' : 'Certificate uploaded')
                    : (lang === 'zh' ? '未上載證書' : 'No certificate yet')}</small>
                </span>
                {badges}
                <Icon name="chev-r" size={16} />
              </button>;
            }
            return <div key={item.id} className="qualification-list-card qualification-list-card--static">
              {code}
              <span className="qualification-list-copy">
                <strong>{name}</strong>
                <small>{validTo}</small>
              </span>
              {badges}
            </div>;
          }

          // The one action that IS completable today: Profile owns licence facts,
          // so 補上資料 goes where the missing renewal date can actually be typed.
          return <button key={item.id} type="button" className="qualification-list-card"
            onClick={() => onEditLicence?.(item)}>
            {code}
            <span className="qualification-list-copy">
              <strong>{name}</strong>
              <small>{lang === 'zh' ? '尚欠續期資料 · 補上資料' : 'Renewal details missing · Add them'}</small>
            </span>
            {badges}
            <Icon name="chev-r" size={16} />
          </button>;
        }
        const q = item.source;
        const status = qualificationStatus(q);
        const version = q.currentVersion || q.versions?.find(x => x.isCurrent);
        const category = qualificationCategory(q.kind);
        return <button key={item.id} type="button" className="qualification-list-card" onClick={() => setSelected(q)}>
          <span className="qualification-list-code">{category.code}</span>
          <span className="qualification-list-copy"><strong>{qualificationTitle(q, lang)}</strong><small>{qualificationKindLabel(q.kind, lang)} · {version?.doesNotExpire ? (lang === 'zh' ? '不設到期日' : 'No expiry') : (lang === 'zh' ? '有效期至 ' : 'Expires ') + (fmtDate(version?.expiresOn, lang) || '—')}</small></span>
          {['expired', 'due_7', 'due_30', 'due_90'].includes(status.key) && <span className="cap-pill tone-warning">{status.key === 'expired' ? (lang === 'zh' ? '已到期' : 'Expired') : (lang === 'zh' ? `${status.days}日` : `${status.days}d`)}</span>}<Icon name="chev-r" size={16} />
        </button>;
      })}
      {candidates.length > 0 && <div className="qualification-candidates"><div className="eyebrow">{lang === 'zh' ? '可加入的 CNE 證書' : 'CNE certificates you can add'}</div>{candidates.slice(0, 3).map(record => <button key={record.id} type="button" className="qualification-candidate-card" onClick={() => setEditor({ mode: 'add', record })}><span><strong>{L(record.title, lang)}</strong><small>{lang === 'zh' ? `建議分類：${qualificationKindLabel(qualificationKindFromTitle(L(record.title, 'en')), lang)}` : `Suggested category: ${qualificationKindLabel(qualificationKindFromTitle(L(record.title, 'en')), lang)}`}</small></span><Icon name="plus" size={16} /></button>)}</div>}
      {editor && <QualificationEditorSheet lang={lang} mode={editor.mode} qualification={editor.qualification || (editor.record ? { kind: qualificationKindFromTitle(L(editor.record.title, 'en')) || 'other', title: editor.record.title, versions: [{ expiresOn: editor.record.expiresOn, sourceRecordId: editor.record.id }], currentVersion: { expiresOn: editor.record.expiresOn, sourceRecordId: editor.record.id } } : null)} onClose={() => setEditor(null)} onSave={(payload, file) => onSave?.({ ...payload, sourceRecordId: editor.record?.id, evidencePath: editor.record?.certificateUrl }, file)} onUpdate={onUpdate} />}
      {evidenceFor && <LicenceEvidenceSheet lang={lang} item={evidenceFor} userId={userId}
        evidence={licenceEvidence} onClose={() => setEvidenceFor(null)} onChanged={onEvidenceChanged} />}
      {selected && <QualificationDetailSheet lang={lang} qualification={qualifications.find(q => q.id === selected.id) || selected} userId={userId} onClose={() => setSelected(null)} onSave={onSave} onUpdate={onUpdate} onArchive={onArchive} onDeleteVersion={onDeleteVersion} onEvidenceChanged={onEvidenceChanged} />}
    </div>
  );
}

Object.assign(window, {
  QUALIFICATION_CATEGORIES,
  QualificationCategoryPicker,
  QualificationEditorSheet,
  QualificationDetailSheet,
  QualificationsView,
  LicenceEvidenceSheet,
  QualificationVersionEvidence,
  EvidenceViewer,
  evidenceIsPdf,
  resolveFocusIntent,
  walletItemRank,
  editorQualificationKind,
  normaliseQualificationKind,
  qualificationKindLabel,
  qualificationSaveErrorMessage,
});

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { QUALIFICATION_CATEGORIES, QUALIFICATION_PRESETS, LEGACY_QUALIFICATION_KINDS, normaliseQualificationKind, editorQualificationKind, qualificationSaveErrorMessage, qualificationCategory, qualificationKindLabel, qualificationTitle, QualificationCategoryPicker, QualificationCategorySummary, QualificationNamePresets, QualificationEvidenceField, QualificationEditorSheet, QualificationVersionEvidence, QualificationDetailSheet, QualificationWalletCard, walletItemRank, resolveFocusIntent, evidenceIsPdf, EvidenceViewer, LicenceEvidenceSheet, QualificationsView });
